/**
 * Surface user-visible toasts for playback and other errors.
 *
 * TEM-870 — replaces silent handleError logging in AudioPlayerContext.
 * Provides a 30s dedupe window keyed on a stable token (typically the
 * stream id) so retry loops don't spam.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ErrorToast, ToastAction } from '../components/ErrorToast';

interface ToastState {
  message: string;
  action?: ToastAction;
}

interface ShowToastOptions {
  /** Token used for dedupe — typically the streamId. Identical tokens
   * within `dedupeWindowMs` are suppressed. */
  dedupeToken?: string;
  /** Dedupe window in milliseconds (default 30_000). */
  dedupeWindowMs?: number;
  /** Optional action button. */
  action?: ToastAction;
}

interface ErrorToastContextType {
  showToast: (message: string, options?: ShowToastOptions) => void;
  dismissToast: () => void;
}

// Default no-op fallback so consumers (e.g. AudioPlayerProvider) work
// when used outside of the provider — primarily for existing unit tests
// that mount AudioPlayerProvider directly. In real app usage the
// provider is mounted at the top of <App />.
const NOOP_CONTEXT: ErrorToastContextType = {
  showToast: () => {},
  dismissToast: () => {},
};

const ErrorToastContext = createContext<ErrorToastContextType>(NOOP_CONTEXT);

export const useErrorToast = (): ErrorToastContextType => {
  return useContext(ErrorToastContext);
};

export const ErrorToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toast, setToast] = useState<ToastState | null>(null);
  const lastShownAt = useRef<Map<string, number>>(new Map());

  const dismissToast = useCallback(() => {
    setToast(null);
  }, []);

  const showToast = useCallback(
    (message: string, options?: ShowToastOptions) => {
      const dedupeToken = options?.dedupeToken;
      const window = options?.dedupeWindowMs ?? 30_000;
      if (dedupeToken) {
        const prev = lastShownAt.current.get(dedupeToken);
        const now = Date.now();
        if (prev && now - prev < window) {
          return; // suppress within dedupe window
        }
        lastShownAt.current.set(dedupeToken, now);
      }
      setToast({ message, action: options?.action });
    },
    [],
  );

  const value = useMemo<ErrorToastContextType>(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast],
  );

  return (
    <ErrorToastContext.Provider value={value}>
      {children}
      {toast && (
        <ErrorToast
          message={toast.message}
          action={toast.action}
          onDismiss={dismissToast}
        />
      )}
    </ErrorToastContext.Provider>
  );
};
